import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  TextField,
  Box,
  LinearProgress,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { getRrwebEventsBySessionAndUser } from '../../service/rrwebService';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';

const IntentReviewModal = ({ open, onComplete, sessionId, userId }) => {
  const [interactions, setInteractions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [intentOptions, setIntentOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [customIntent, setCustomIntent] = useState('');
  const [rrwebEvents, setRrwebEvents] = useState([]);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch interactions
        const interactionsResponse = await axios.get(
          `${process.env.REACT_APP_HOST_API}/interactions/session/${sessionId}/user/${userId}`
        );
        setInteractions(interactionsResponse.data);

        // Fetch rrweb events using service
        try {
          const events = await getRrwebEventsBySessionAndUser(sessionId, userId);
          setRrwebEvents(events || []);
        } catch (rrwebError) {
          console.log('📹 Video replay not available');
          setRrwebEvents([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    if (open && sessionId && userId) {
      fetchData();
    }
  }, [open, sessionId, userId]);

  useEffect(() => {
    const fetchIntentOptions = async () => {
      if (!interactions || interactions.length === 0) {
        console.log('⏳ No interactions loaded yet');
        return;
      }
      
      const currentInteraction = interactions[currentIndex];
      
      if (!currentInteraction) {
        console.log('⏳ No current interaction yet');
        return;
      }
      
      console.log('🔍 Fetching intent options for interaction:', currentInteraction.id);
      setLoadingOptions(true);
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_HOST_API}/interactions/${currentInteraction.id}/intent-options`
        );
        console.log('✅ Intent options received:', response.data);
        setIntentOptions(response.data.options || []);
      } catch (error) {
        console.error('❌ Error fetching intent options:', error);
        // Fallback options
        setIntentOptions([
          "I was trying to debug an error in my code",
          "I wanted to understand how to implement something",
          "I was checking if my approach was correct"
        ]);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchIntentOptions();
  }, [currentIndex, interactions.length]);

  // Handle completion when no interactions
  useEffect(() => {
    if (!loading && interactions.length === 0) {
      onComplete();
    }
  }, [loading, interactions, onComplete]);

  // Initialize replay when interaction changes
  useEffect(() => {
    if (currentInteraction && rrwebEvents.length > 0 && containerRef.current) {
      initializeReplay();
    }
  }, [currentIndex, rrwebEvents]);

  const currentInteraction = interactions[currentIndex];
  const progress = interactions.length > 0 ? ((currentIndex + 1) / interactions.length) * 100 : 0;

  const initializeReplay = () => {
    if (!currentInteraction || rrwebEvents.length === 0) return;

    // Clear existing player
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    try {
      const interactionTime = new Date(currentInteraction.created_time).getTime();
      
      const relevantEvents = rrwebEvents.filter(event => {
        const eventTime = event.timestamp;
        return eventTime >= interactionTime - 30000 && eventTime <= interactionTime + 10000;
      });

      if (relevantEvents.length === 0) {
        console.warn('No events found for this interaction');
        return;
      }

      playerRef.current = new rrwebPlayer({
        target: containerRef.current,
        props: {
          events: relevantEvents,
          width: containerRef.current.offsetWidth,
          height: 600,
          autoPlay: false,
          showController: true,
          speedOption: [1, 2, 4],
        },
      });
    } catch (error) {
      console.error('Error initializing replay:', error);
    }
  };

  const handleNext = async () => {
    const finalIntent = selectedOption === 'other' ? customIntent : selectedOption;
    
    if (!finalIntent.trim()) return;

    const newResponses = {
      ...responses,
      [currentInteraction.id]: finalIntent
    };
    setResponses(newResponses);

    try {
      await axios.post(
        `${process.env.REACT_APP_HOST_API}/interactions/${currentInteraction.id}/student-intent`,
        { studentReportedIntent: finalIntent }
      );
    } catch (error) {
      console.error('Error saving student intent:', error);
    }

    if (currentIndex < interactions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption('');
      setCustomIntent('');
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      const previousResponse = responses[interactions[currentIndex - 1].id] || '';
      
      // Check if previous response matches one of the options
      if (intentOptions.includes(previousResponse)) {
        setSelectedOption(previousResponse);
        setCustomIntent('');
      } else {
        setSelectedOption('other');
        setCustomIntent(previousResponse);
      }
    }
  };

  const isNextDisabled = () => {
    if (selectedOption === 'other') {
      return !customIntent.trim();
    }
    return !selectedOption;
  };

  // Early returns AFTER all hooks
  if (!open) return null;
  
  if (loading) {
    return (
      <Dialog open={true}>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>Loading your interactions...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (interactions.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth="xl" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        Review Your AI Interactions ({currentIndex + 1} of {interactions.length})
      </DialogTitle>

      <LinearProgress variant="determinate" value={progress} />

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {rrwebEvents.length > 0 
               ? "Watch the video replay and describe why you decided to ask the AI for help."
               : "Review your interaction and describe why you decided to ask the AI for help."}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Left side: Video or Details */}
          <Grid item xs={12} md={8}>
            {rrwebEvents.length > 0 ? (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  📹 Session Replay (30 seconds before your question):
                </Typography>
                <Box 
                  ref={containerRef} 
                  sx={{ 
                    width: '100%',
                    minHeight: 600,
                    bgcolor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    mb: 1
                  }}
                />
              </Paper>
            ) : (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Your code at the time:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, maxHeight: 200, overflow: 'auto', bgcolor: '#f5f5f5' }}>
                  <pre style={{ margin: 0, fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {currentInteraction.code_snapshot || 'No code snapshot available'}
                  </pre>
                </Paper>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  What you asked:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
                  <Typography>
                    {currentInteraction.message_history?.[0]?.content || 'No question recorded'}
                  </Typography>
                </Paper>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  AI's response:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
                  <Typography>
                    {currentInteraction.message_history?.[1]?.content || 'No response recorded'}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Grid>

          {/* Right side: Intent selection */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                What were you trying to do?
              </Typography>
              
              {loadingOptions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <FormControl component="fieldset" sx={{ flexGrow: 1 }}>
                  <RadioGroup
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  >
                    {intentOptions.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        value={option}
                        control={<Radio />}
                        label={option}
                        sx={{ 
                          mb: 2, 
                          p: 1.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                      />
                    ))}
                    
                    <FormControlLabel
                      value="other"
                      control={<Radio />}
                      label="Other (describe below)"
                      sx={{ 
                        mb: 1,
                        p: 1.5,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                    />
                  </RadioGroup>

                  {selectedOption === 'other' && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={customIntent}
                      onChange={(e) => setCustomIntent(e.target.value)}
                      placeholder="Describe why you decided to ask the AI for help..."
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  )}
                </FormControl>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleBack} disabled={currentIndex === 0}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={isNextDisabled()}
        >
          {currentIndex < interactions.length - 1 ? 'Next' : 'Finish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntentReviewModal;